import { LoanDetails, Mimshak, PolicyOrAccount, PolicyStatus, Product, InterestType, RepaymentType, LinkageType, RepaymentFrequency } from "../interfaces/DTO/mislaka_xml_models";
import { ManualMimshakData } from "../interfaces/DTO/manualMimshakData";

/**
 * Loan Mimshak Builder
 * =====================
 *
 * יוצר מבנה Mimshak רזה להלוואות שמשתמש בשדה data של ManualMimshakData.
 *
 * המבנה:
 * Mimshak -> Product -> PolicyOrAccount -> LoanDetails
 *
 * דוגמת שימוש:
 * ```typescript
 * const loanData: ManualMimshakData = {
 *   product_type: ManualProductType.LOAN,
 *   handler_code: "12",
 *   policy_number: "LOAN123456",
 *   funds_sum: 500000, // יתרת הלוואה
 *   withdrawal_amount: 3000, // החזר חודשי
 *   join_date: "2024-01-01",
 *   // ... שדות נוספים
 * };
 *
 * const mimshak = buildLoanMimshak(loanData);
 * // השדה data של ManualMimshakData יכיל את ה-mimshak המקודד
 * ```
 */

/**
 * יוצר אובייקט LoanDetails מתוך ManualMimshakData
 *
 * אם יש data.products[0].policies_or_accounts[0].loan_details[0] קיים - משתמש בו
 * אחרת - בונה מהשדות הבסיסיים
 *
 * @param loanData - נתוני ההלוואה הידנית
 * @returns אובייקט LoanDetails עם הנתונים המלאים
 */
export const buildLoanDetails = (loanData: ManualMimshakData): LoanDetails => {
  // אם יש data עם loan_details - השתמש בו
  const existingLoanDetails = loanData.data?.products?.[0]?.policies_or_accounts?.[0]?.loan_details?.[0];

  if (existingLoanDetails) {
    return existingLoanDetails;
  }

  // אחרת - בנה מהשדות הבסיסיים עם ערכי ברירת מחדל
  return {
    loan_start_date: loanData.join_date || undefined, // תאריך תחילת ההלוואה
    loan_balance: loanData.funds_sum || 0, // יתרת ההלוואה
    periodic_repayment_amount: loanData.withdrawal_amount || 0, // החזר חודשי
    
    interest_type: InterestType.FIXED, // ריבית קבועה כברירת מחדל
    repayment_type: RepaymentType.SPITZER, // שפיצר כברירת מחדל
    linkage_type: LinkageType.NONE, // ללא הצמדה כברירת מחדל
    repayment_frequency: RepaymentFrequency.MONTHLY, // תשלום חודשי כברירת מחדל
  };
};

/**
 * יוצר אובייקט PolicyOrAccount רזה להלוואה
 */
export const buildLoanPolicyOrAccount = (loanData: ManualMimshakData): PolicyOrAccount => {
  return {
    policy_number: loanData.policy_number,
    status: PolicyStatus.ACTIVE, // סטטוס פעיל
    update_date: loanData.reported_period, // תאריך עדכון - התקופה המדווחת
    program_name: "הלוואה", // שם התוכנית
    investment_paths: [], // רשימה רזה - להלוואה אין מסלולי השקעה
    join_date: loanData.join_date || undefined, // תאריך הצטרפות
    loan_details: [buildLoanDetails(loanData)],
  };
};

/**
 * יוצר אובייקט Product רזה להלוואה
 */
export const buildLoanProduct = (loanData: ManualMimshakData): Product => {
  return {
    details: {
      producer_code: "",
      handler_code: loanData.handler_code,
      product_type: loanData.product_type,
    },
    policies_or_accounts: [buildLoanPolicyOrAccount(loanData)],
  };
};

/**
 * יוצר אובייקט Mimshak רזה להלוואה
 */
export const buildLoanMimshak = (loanData: ManualMimshakData): Mimshak => {
  return {
    products: [buildLoanProduct(loanData)],
  };
};
